import MarketingLayout from '../layouts/MarketingLayout.jsx'
import SEOHead from '../components/SEOHead.jsx'
import CTAButton from '../components/CTAButton.jsx'
import StatsRow from '../components/StatsRow.jsx'

const pageJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'KartQuiz pour les profs et formateurs',
  description:
    'Conçu avec des enseignants, pour des enseignants. Tu prépares moins, tu animes mieux, tu vois ce qui coince.',
  url: 'https://kartquiz.fr/pour-les-profs',
}

const DISCIPLINES = [
  {
    emoji: '📐',
    label: 'Maths',
    text: 'Entraînement aux automatismes, formules, tables, dérivées — répétition espacée pour ancrer les réflexes de calcul avant les examens.',
  },
  {
    emoji: '🌍',
    label: 'Histoire-Géo',
    text: 'Dates, repères, frises chronologiques, capitales, grands thèmes du programme — quiz express en début ou fin de séance.',
  },
  {
    emoji: '🔤',
    label: 'Langues vivantes',
    text: 'Vocabulaire, conjugaison, grammaire, expressions idiomatiques — le timer min empêche les clics au hasard et force la réflexion.',
  },
  {
    emoji: '🔬',
    label: 'SVT / Physique-Chimie',
    text: 'Concepts, définitions, schémas, calculs numériques — structurer les révisions par sous-thème pour isoler les notions fragiles.',
  },
  {
    emoji: '📚',
    label: 'Philo / Lettres',
    text: 'Citations, courants philosophiques, auteurs, œuvres au programme — formater les questions sous forme de reconnaissance de source ou d\'auteur.',
  },
  {
    emoji: '💼',
    label: 'Sciences éco / Droit',
    text: 'Définitions, mécanismes économiques, jurisprudence, notions de SES — quiz rapide sur les fondamentaux avant chaque chapitre avancé.',
  },
  {
    emoji: '🏥',
    label: 'Médecine / IFSI',
    text: 'QCM massifs, ECN, épreuves de pharmacologie, anatomie — quiz de 30 questions en 20 minutes pour simuler la pression de l\'examen.',
  },
]

const MOMENTS = [
  {
    emoji: '🌅',
    label: 'Brise-glace en début de cours',
    text: 'Un quiz de 5 questions sur la séance précédente. Les élèves arrivent, ils jouent, tu récupères l\'attention en 3 minutes.',
  },
  {
    emoji: '📖',
    label: 'Révision de fin de chapitre',
    text: 'Un quiz complet sur les notions clés du chapitre. La synthèse de fin identifie ce que tu dois retravailler avant de passer à la suite.',
  },
  {
    emoji: '🔍',
    label: 'Évaluation diagnostique',
    text: 'Teste les acquis en début d\'année ou avant un chapitre. Ajuste ton programme selon ce que la classe maîtrise déjà.',
  },
  {
    emoji: '🎯',
    label: 'Préparation à un examen blanc',
    text: 'Mets la pression en mode compétition. Le podium final motive même les élèves qui décrochent d\'habitude.',
  },
]

function CTASection() {
  return (
    <section className="bg-kart-night py-20">
      <div className="max-w-3xl mx-auto px-6 text-center space-y-6">
        <h2 className="font-display font-semibold text-3xl md:text-4xl text-white">
          Prêt à mettre ta classe sur la grille de départ ?
        </h2>
        <p className="text-slate-300 text-lg">
          Créer ton compte prend 30 secondes. La première course peut démarrer dans la foulée.
        </p>
        <CTAButton to="/login" size="lg">Créer un compte gratuit</CTAButton>
        <p className="text-slate-500 text-sm">Sans carte bancaire · 100 % gratuit pendant la bêta</p>
      </div>
    </section>
  )
}

export default function ForTeachersPage() {
  return (
    <MarketingLayout>
      <SEOHead
        title="KartQuiz pour les profs et formateurs | KartQuiz"
        description="Conçu pour les profs : prépare moins, anime mieux, identifie les notions à retravailler. Collège, lycée, supérieur."
        canonical="https://kartquiz.fr/pour-les-profs"
        jsonLd={[pageJsonLd]}
      />

      {/* Hero */}
      <section className="bg-[#F8FAFC] py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-6 text-center space-y-6">
          <p className="text-kart-action font-semibold text-sm uppercase tracking-widest">Pour les enseignants</p>
          <h1 className="font-display font-semibold text-4xl md:text-5xl text-kart-night">
            KartQuiz pour les profs et formateurs
          </h1>
          <p className="text-slate-500 text-xl leading-relaxed">
            Conçu avec des enseignants, pour des enseignants. Tu prépares moins, tu animes mieux, tu vois ce qui coince.
          </p>
          <CTAButton to="/login" size="lg">Créer un compte gratuit</CTAButton>
        </div>
      </section>

      {/* 4 pillars */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">

            <div className="bg-[#F8FAFC] rounded-2xl p-8 space-y-4 border border-slate-200">
              <div className="w-12 h-12 bg-kart-action/10 rounded-xl flex items-center justify-center text-2xl">✏️</div>
              <h2 className="font-display font-semibold text-xl text-kart-night">Tu prépares moins</h2>
              <p className="text-slate-600 leading-relaxed">
                Tape un sujet, le quiz est généré par l'IA en 30 secondes. Tu relis, tu ajustes deux questions si besoin, tu lances. La bibliothèque communauté te donne accès aux quiz créés par d'autres profs sur le même programme.
              </p>
              <p className="text-slate-500 text-sm">
                Fini les soirées à taper des QCM. L'IA s'occupe du brouillon, toi tu valides.
              </p>
            </div>

            <div className="bg-[#F8FAFC] rounded-2xl p-8 space-y-4 border border-slate-200">
              <div className="w-12 h-12 bg-kart-blue/10 rounded-xl flex items-center justify-center text-2xl">🏎</div>
              <h2 className="font-display font-semibold text-xl text-kart-night">Tu animes mieux</h2>
              <p className="text-slate-600 leading-relaxed">
                La course maintient l'attention bien au-delà d'un quiz classique. Les bonus d'attaque créent des rebondissements — le 1er peut se faire doubler à la dernière question. Même les élèves qui décrochent d'habitude restent dans la partie.
              </p>
              <p className="text-slate-500 text-sm">
                L'engagement moyen sur une partie KartQuiz : toute la séance, sans relâche.
              </p>
            </div>

            <div className="bg-[#F8FAFC] rounded-2xl p-8 space-y-4 border border-slate-200">
              <div className="w-12 h-12 bg-kart-teal/10 rounded-xl flex items-center justify-center text-2xl">📊</div>
              <h2 className="font-display font-semibold text-xl text-kart-night">Tu vois ce qui coince</h2>
              <p className="text-slate-600 leading-relaxed">
                À la fin de la course, tu vois en un coup d'œil le taux de réussite par sous-thème et par question. Tu identifies les notions à retravailler avant le prochain cours — sans dépouiller des copies.
              </p>
              <p className="text-slate-500 text-sm">
                Chaque élève reçoit aussi sa synthèse personnalisée : Maîtrisé / Acquis / À réviser.
              </p>
            </div>

            <div className="bg-[#F8FAFC] rounded-2xl p-8 space-y-4 border border-slate-200">
              <div className="w-12 h-12 bg-kart-yellow/20 rounded-xl flex items-center justify-center text-2xl">🎮</div>
              <h2 className="font-display font-semibold text-xl text-kart-night">Tu gardes la main</h2>
              <p className="text-slate-600 leading-relaxed">
                Tu choisis le rythme : timer min (empêche les clics au hasard) et timer max (limite la durée par question). Tu peux pauser, relancer, choisir le nombre de tours. La vitesse adaptative peut être désactivée si tu préfères un avancement uniforme.
              </p>
              <p className="text-slate-500 text-sm">
                Toutes les options se configurent à la création de la partie, en 30 secondes.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* Disciplines */}
      <section className="py-20 md:py-28 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-display font-semibold text-3xl md:text-4xl text-kart-night mb-4">
              KartQuiz fonctionne dans toutes les matières
            </h2>
            <p className="text-slate-500 text-lg max-w-xl mx-auto">
              Du collège à l'enseignement supérieur, pour toutes les disciplines qui passent par des QCM.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {DISCIPLINES.map(d => (
              <div key={d.label} className="bg-white border border-slate-200 rounded-2xl p-6 space-y-3">
                <div className="text-3xl">{d.emoji}</div>
                <h3 className="font-display font-semibold text-kart-night">{d.label}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{d.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Moments */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-display font-semibold text-3xl md:text-4xl text-kart-night mb-4">
              À n'importe quel moment du cours
            </h2>
            <p className="text-slate-500 text-lg max-w-xl mx-auto">
              KartQuiz s'insère dans ta séance sans la déstructurer.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {MOMENTS.map(m => (
              <div key={m.label} className="flex gap-5 bg-[#F8FAFC] border border-slate-200 rounded-2xl p-6">
                <div className="text-3xl shrink-0">{m.emoji}</div>
                <div className="space-y-2">
                  <h3 className="font-display font-semibold text-kart-night">{m.label}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{m.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social proof — stat row */}
      <StatsRow />

      {/* Testimonials placeholder */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center space-y-4">
          <h2 className="font-display font-semibold text-3xl text-kart-night">Ce qu'en disent les profs</h2>
          <p className="text-slate-500">
            KartQuiz est en bêta — les premiers témoignages arrivent. En attendant,{' '}
            <a href="/login" className="text-kart-blue hover:underline font-medium">essaie par toi-même</a> 🏁
          </p>
        </div>
      </section>

      <CTASection />
    </MarketingLayout>
  )
}
